import React, { MouseEvent } from "react";

import { Fields, useAnalytics } from "@accurx/analytics";
import { useCurrentWorkspace } from "@accurx/auth";
import { ButtonLink, Ds } from "@accurx/design";
import { BatchPageContent } from "domains/batch-messaging/components/BatchLayout";
import { BATCH_MESSAGING_ROUTES } from "domains/batch-messaging/routes";
import { generatePath, useHistory } from "react-router";

export const BatchSelectPage = () => {
    return (
        <BatchPageContent>
            <Ds.Box mb="3">
                <Ds.Flex justifyContent="space-between" alignItems="center">
                    <Ds.Text as="h1" size="large" weight="bold">
                        Select patients to contact
                    </Ds.Text>
                    <Ds.Link
                        href="https://support.accurx.com/en/articles/7991293-accurx-web-how-to-send-batch-messages"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        How does batch messaging work?
                    </Ds.Link>
                </Ds.Flex>
            </Ds.Box>
            <Ds.Flex flexDirection="column" gap="2">
                <BatchSelectCard
                    text="Upload a file"
                    ariaLabel="Continue to Import Patients From a File"
                    route={BATCH_MESSAGING_ROUTES.fileUpload}
                    analyticsId="Csv"
                    data-userflow-id={"batch-import-csv"}
                />
                <BatchSelectCard
                    text="Select from your lists in Accurx"
                    ariaLabel="Continue to Import Patients From Patient List"
                    route={BATCH_MESSAGING_ROUTES.importPatientList}
                    analyticsId="PatientList"
                    data-userflow-id={"batch-continue-to-import-patient-list"}
                />
                <BatchSelectCard
                    text="Add patients manually"
                    ariaLabel="Continue to Import Patients From Copy And Paste"
                    route={BATCH_MESSAGING_ROUTES.manualInput}
                    analyticsId="CopyPaste"
                    data-userflow-id={"batch-continue-to-copy-paste-patients"}
                />
            </Ds.Flex>
        </BatchPageContent>
    );
};

const BatchSelectCard = ({
    text,
    ariaLabel,
    route,
    analyticsId,
}: {
    text: string;
    ariaLabel: string;
    route: string;
    analyticsId: Extract<Fields, { key: "source" }>["value"];
}) => {
    const history = useHistory();
    const track = useAnalytics();
    const { orgId: workspaceId } = useCurrentWorkspace();

    const path = generatePath(route, { workspaceId });

    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        history.push(path);
        track("BatchPatientListSource Button Click", {
            source: analyticsId,
            isTrustFlow: true,
        });
    };

    return (
        <Ds.Card appearance="elevated">
            <Ds.Card.Body>
                <Ds.Flex justifyContent="space-between" alignItems="center">
                    <Ds.Text as="h2" weight="bold">
                        {text}
                    </Ds.Text>
                    <ButtonLink
                        href={generatePath(path, { workspaceId })}
                        text="Continue"
                        theme="secondary"
                        icon={{
                            name: "ArrowTail",
                            colour: "blue",
                            rotation: "right",
                            placement: "end",
                        }}
                        aria-label={ariaLabel}
                        onClick={handleClick}
                    />
                </Ds.Flex>
            </Ds.Card.Body>
        </Ds.Card>
    );
};
